import React, { useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import { toast } from 'react-toastify';
import api from '../../../services/api';

const SettingsPage = () => {
  const [settings, setSettings] = useState({
    siteName: 'Kenya Tours & Travel Agency',
    siteEmail: 'info@kenyatours.com',
    sitePhone: '+254 712 345 678',
    siteAddress: 'Nairobi, Kenya',
    currency: 'USD',
    defaultLanguage: 'en',
    enableBookingNotifications: true,
    enablePaymentNotifications: true,
    maintenanceMode: false
  });

  const [isEditing, setIsEditing] = useState(false);

  // Fetch settings
  const { isLoading, isError, refetch } = useQuery(
    'settings',
    async () => {
      try {
        const response = await api.get('/api/v1/admin/settings');
        
        if (response.data && response.data.data) {
          setSettings(response.data.data);
        }
        return response.data;
      } catch (error) {
        console.error('Error fetching settings:', error);
        // We'll continue using the default settings if the API call fails
        return null;
      }
    },
    {
      onError: (error) => {
        console.error('Error fetching settings:', error);
      }
    }
  );

  // Update settings mutation
  const updateSettingsMutation = useMutation(
    async (updatedSettings) => {
      const response = await api.put('/api/v1/admin/settings', updatedSettings);
      return response.data;
    },
    {
      onSuccess: () => {
        toast.success('Settings updated successfully');
        setIsEditing(false);
        refetch();
      },
      onError: (error) => {
        console.error('Error updating settings:', error);
        toast.error('Failed to update settings. Please try again.');
      }
    }
  );

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSettings({
      ...settings,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    updateSettingsMutation.mutate(settings);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-gray-900">System Settings</h1>
        {!isEditing ? (
          <button
            onClick={() => setIsEditing(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
          >
            Edit Settings
          </button>
        ) : null}
      </div>

      {isLoading ? (
        <div className="animate-pulse p-6">
          <div className="h-10 bg-gray-200 rounded mb-4"></div>
          <div className="h-10 bg-gray-200 rounded mb-4"></div>
          <div className="h-10 bg-gray-200 rounded mb-4"></div>
        </div>
      ) : isError ? (
        <div className="p-6 text-center">
          <div className="text-red-500 mb-4">Error loading settings. Please try again.</div>
          <button
            onClick={() => refetch()}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
          >
            Retry
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="bg-white shadow rounded-lg divide-y divide-gray-200">
          {/* General Settings */}
          <div className="p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">General Settings</h2>
            <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-2">
              <div>
                <label htmlFor="siteName" className="block text-sm font-medium text-gray-700">
                  Site Name
                </label>
                <input
                  type="text"
                  name="siteName"
                  id="siteName"
                  value={settings.siteName}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
                />
              </div>
              <div>
                <label htmlFor="siteEmail" className="block text-sm font-medium text-gray-700">
                  Site Email
                </label>
                <input
                  type="email"
                  name="siteEmail"
                  id="siteEmail"
                  value={settings.siteEmail}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
                />
              </div>
              <div>
                <label htmlFor="sitePhone" className="block text-sm font-medium text-gray-700">
                  Site Phone
                </label>
                <input
                  type="text"
                  name="sitePhone"
                  id="sitePhone"
                  value={settings.sitePhone}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
                />
              </div>
              <div>
                <label htmlFor="siteAddress" className="block text-sm font-medium text-gray-700">
                  Site Address
                </label>
                <input
                  type="text"
                  name="siteAddress"
                  id="siteAddress"
                  value={settings.siteAddress}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
                />
              </div>
            </div>
          </div>

          {/* Localization Settings */}
          <div className="p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Localization Settings</h2>
            <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-2">
              <div>
                <label htmlFor="currency" className="block text-sm font-medium text-gray-700">
                  Currency
                </label>
                <select
                  id="currency"
                  name="currency"
                  value={settings.currency}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm rounded-md"
                >
                  <option value="USD">US Dollar (USD)</option>
                  <option value="KES">Kenyan Shilling (KES)</option>
                  <option value="EUR">Euro (EUR)</option>
                  <option value="GBP">British Pound (GBP)</option>
                </select>
              </div>
              <div>
                <label htmlFor="defaultLanguage" className="block text-sm font-medium text-gray-700">
                  Default Language
                </label>
                <select
                  id="defaultLanguage"
                  name="defaultLanguage"
                  value={settings.defaultLanguage}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm rounded-md"
                >
                  <option value="en">English</option>
                  <option value="sw">Swahili</option>
                  <option value="fr">French</option>
                  <option value="de">German</option>
                </select>
              </div>
            </div>
          </div>

          {/* Notification Settings */}
          <div className="p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Notification Settings</h2>
            <div className="space-y-4">
              <div className="flex items-start">
                <div className="flex items-center h-5">
                  <input
                    id="enableBookingNotifications"
                    name="enableBookingNotifications"
                    type="checkbox"
                    checked={settings.enableBookingNotifications}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    className="focus:ring-primary h-4 w-4 text-primary border-gray-300 rounded"
                  />
                </div>
                <div className="ml-3 text-sm">
                  <label htmlFor="enableBookingNotifications" className="font-medium text-gray-700">
                    Enable Booking Notifications
                  </label>
                  <p className="text-gray-500">Receive email notifications when new bookings are made.</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="flex items-center h-5">
                  <input
                    id="enablePaymentNotifications"
                    name="enablePaymentNotifications"
                    type="checkbox"
                    checked={settings.enablePaymentNotifications}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    className="focus:ring-primary h-4 w-4 text-primary border-gray-300 rounded"
                  />
                </div>
                <div className="ml-3 text-sm">
                  <label htmlFor="enablePaymentNotifications" className="font-medium text-gray-700">
                    Enable Payment Notifications
                  </label>
                  <p className="text-gray-500">Receive email notifications when payments are processed.</p>
                </div>
              </div>
            </div>
          </div>

          {/* System Settings */}
          <div className="p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">System Settings</h2>
            <div className="space-y-4">
              <div className="flex items-start">
                <div className="flex items-center h-5">
                  <input
                    id="maintenanceMode"
                    name="maintenanceMode"
                    type="checkbox"
                    checked={settings.maintenanceMode}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    className="focus:ring-primary h-4 w-4 text-primary border-gray-300 rounded"
                  />
                </div>
                <div className="ml-3 text-sm">
                  <label htmlFor="maintenanceMode" className="font-medium text-gray-700">
                    Maintenance Mode
                  </label>
                  <p className="text-gray-500">Put the website in maintenance mode. Only administrators will be able to access the site.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Form Actions */}
          {isEditing && (
            <div className="px-6 py-4 bg-gray-50 flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => setIsEditing(false)}
                className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                disabled={updateSettingsMutation.isLoading}
              >
                {updateSettingsMutation.isLoading ? 'Saving...' : 'Save Settings'}
              </button>
            </div>
          )}
        </form>
      )}
    </div>
  );
};

export default SettingsPage;
